import { TransactionEntry } from '@services/api/billing/transaction.api'
import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/index'
import {
  fetchTransactionEntries,
  setTransactionPagination,
} from '@store/actions/billing.action'
import { SortDirection } from '@constants/Common/sort-direction.constant'
import { buildTransactionFilters } from '../filterBuilders'

interface UseTransactionHooks {
  transactions: TransactionEntry[]
  transactionCount: number
  loadingTransactions: boolean
  currentPage: number
  pageSize: number
  handlePageChange: (page: number) => void
  handlePageSizeChange: (pageSize: number) => void
  handleSortChange: (sort: string) => void
  handleTableSortChange: (sort: {
    key: string
    direction: SortDirection
  }) => void
}

const useTransaction = (
  searchQuery: string,
  filters: Record<string, string[]>,
): UseTransactionHooks => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    transactions,
    transactionCount,
    loadingTransactions,
    transactionPagination,
  } = useSelector((state: RootState) => state.billing)
  const { currentPage, pageSize, sort } = transactionPagination

  const selectedPatient = useSelector(
    (state: RootState) => state.patient.selectedPatient,
  )
  const selectedClinic = useSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )

  useEffect(() => {
    dispatch(setTransactionPagination({ currentPage: 1 }))
  }, [
    dispatch,
    selectedPatient?.patient_id,
    selectedClinic?.clinic_id,
    searchQuery,
  ])

  useEffect(() => {
    const queryFilters = buildTransactionFilters({
      search: searchQuery,
      tabFilters: {
        transaction_type: filters.transaction_type ?? [],
      },
      sort,
    })
    dispatch(fetchTransactionEntries(queryFilters))
  }, [
    dispatch,
    currentPage,
    pageSize,
    selectedPatient?.patient_id,
    selectedClinic?.clinic_id,
    filters.transaction_type,
    searchQuery,
    sort,
  ])

  const handlePageChange = useCallback(
    (page: number): void => {
      dispatch(setTransactionPagination({ currentPage: page }))
    },
    [dispatch],
  )

  const handlePageSizeChange = useCallback(
    (newPageSize: number): void => {
      dispatch(
        setTransactionPagination({ pageSize: newPageSize, currentPage: 1 }),
      )
    },
    [dispatch],
  )

  const handleSortChange = useCallback(
    (newSort: string): void => {
      dispatch(setTransactionPagination({ sort: newSort, currentPage: 1 }))
    },
    [dispatch],
  )

  const handleTableSortChange = useCallback(
    ({ key, direction }: { key: string; direction: SortDirection }): void => {
      if (key === 'date' || key === 'patient' || key === 'type') {
        handleSortChange(`${key}:${direction}`)
      }
    },
    [handleSortChange],
  )

  return {
    transactions,
    transactionCount,
    loadingTransactions,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    handleTableSortChange,
  }
}

export default useTransaction
